/**
 * CustomContentCategories — internal module for CustomContentService.
 *
 * Sprint 4 (#54): extracted from CustomContentService.js to keep individual
 * modules under the 800-LOC limit (CLAUDE.md Rule 3). This file owns the
 * category metadata + the implementations of the static category methods
 * that were merged into CustomContentService from CustomContentCategoryService
 * in Sprint 3 (#51).
 *
 * NOTE — Internal module: import from CustomContentService instead. The
 * `_customContent/` prefix signals "do not import directly". The facade in
 * CustomContentService.js re-exposes these via static methods on the class
 * so existing callers (hooks, components, tests) continue to work without
 * changes.
 *
 * The exported functions take an explicit `client` parameter so the facade
 * can manage its own static `client` field (the original behavior — tests
 * reset `CustomContentService.client = null` between assertions).
 */

import {
  BarChart3, BookOpen, Scale, DollarSign, Target, Phone, PenLine, Heart
} from 'lucide-react';
import {
  createNetworkError,
  createValidationError
} from '../../utils/StandardErrorFormat.js';
import logger from '../../utils/logger.js';

const moduleLogger = logger.child('CustomContentService:Categories');

export const DEFAULT_CATEGORIES = [
  {
    name: 'Market Analysis',
    description: 'Market updates, economic analysis, and investment insights',
    icon: 'BarChart3',
    isDefault: true,
    color: '#3B82F6' // blue
  },
  {
    name: 'Client Communication',
    description: 'Greeting templates, meeting follow-ups, and check-in messages',
    icon: 'Phone',
    isDefault: true,
    color: '#8B5CF6' // purple
  },
  {
    name: 'Compliance & Tax',
    description: 'Regulatory updates, tax strategies, and compliance requirements',
    icon: 'Scale',
    isDefault: true,
    color: '#10B981' // green
  },
  {
    name: 'Education',
    description: 'Investment concepts, portfolio education, and financial literacy',
    icon: 'BookOpen',
    isDefault: true,
    color: '#F59E0B' // amber
  },
  {
    name: 'Firm Information',
    description: 'Investment philosophy, firm values, and unique approach',
    icon: 'Target',
    isDefault: true,
    color: '#EF4444' // red
  },
  {
    name: 'Personal Note',
    description: 'Your weekly personal note — update before each batch send. Fills {{personal_note}} in templates.',
    icon: 'PenLine',
    isDefault: true,
    color: '#6366F1' // indigo
  },
  {
    name: 'Human Touch',
    description: 'Book recommendations, community events, personal interests. Fills {{human_touch}} in templates.',
    icon: 'Heart',
    isDefault: true,
    color: '#EC4899' // pink
  }
];

export const ICON_MAP = {
  BarChart3,
  BookOpen,
  Scale,
  DollarSign,
  Target,
  Phone,
  PenLine,
  Heart
};

/**
 * Get default categories (fallback). Pure function — no I/O.
 */
export function getDefaultCategories() {
  return DEFAULT_CATEGORIES.map(cat => ({
    value: cat.name,
    label: cat.name,
    name: cat.name,
    description: cat.description,
    icon: ICON_MAP[cat.icon] || BookOpen,
    iconName: cat.icon,
    color: cat.color,
    isDefault: true
  }));
}

/**
 * Backfill missing default categories into the database.
 * @param {object} client - GraphQL client
 * @param {string} userId
 * @param {Array} missingDefaults
 * @param {Array} existingCategories - mutated in-place to mirror pre-split behavior
 */
export async function backfillCategories(client, userId, missingDefaults, existingCategories) {
  try {
    const now = new Date().toISOString();

    for (const category of missingDefaults) {
      try {
        const { data: created, errors } = await client.models.CustomContentCategory.create({
          userId: userId,
          name: category.name,
          description: category.description,
          icon: category.icon,
          color: category.color,
          isDefault: category.isDefault,
          createdAt: now,
          updatedAt: now
        });

        if (!errors && created) {
          existingCategories.push(created);
          moduleLogger.info(`Backfilled category: ${category.name}`);
        }
      } catch (error) {
        moduleLogger.warn(`Failed to backfill ${category.name}:`, error);
      }
    }
  } catch (error) {
    moduleLogger.warn('Error during backfill:', error);
  }
}

/**
 * Get all categories for the current user. Initializes defaults on first run
 * and backfills any newly-introduced default categories.
 */
export async function getCategoriesImpl(client, userId, instanceLogger = moduleLogger) {
  try {
    const { data: categories, errors } = await client.models.CustomContentCategory.list({
      filter: { userId: { eq: userId } }
    });

    if (errors && errors.length > 0) {
      instanceLogger.error('Error fetching categories:', errors);
      return getDefaultCategories();
    }

    if (!categories || categories.length === 0) {
      instanceLogger.info('No categories found, initializing defaults');
      return await initializeDefaultCategoriesImpl(client, userId, instanceLogger);
    }

    // Deduplicate by name (defensive against legacy duplicates)
    const uniqueCategories = [];
    const seenNames = new Set();

    for (const cat of categories) {
      if (!seenNames.has(cat.name)) {
        seenNames.add(cat.name);
        uniqueCategories.push(cat);
      }
    }

    const missingDefaults = DEFAULT_CATEGORIES.filter(
      dc => !seenNames.has(dc.name)
    );
    if (missingDefaults.length > 0) {
      instanceLogger.info(`Backfilling ${missingDefaults.length} new default categories: ${missingDefaults.map(d => d.name).join(', ')}`);
      await backfillCategories(client, userId, missingDefaults, uniqueCategories);
    }

    instanceLogger.info(`Found ${categories.length} categories, deduplicated to ${uniqueCategories.length}`);

    return uniqueCategories
      .sort((a, b) => (a.isDefault ? -1 : 1))
      .map(cat => ({
        id: cat.id,
        value: cat.name,
        label: cat.name,
        name: cat.name,
        description: cat.description || '',
        icon: ICON_MAP[cat.icon] || BookOpen,
        iconName: cat.icon,
        color: cat.color || '#6B7280',
        isDefault: cat.isDefault || false
      }));
  } catch (error) {
    instanceLogger.error('Error in getCategories:', error);
    return getDefaultCategories();
  }
}

/**
 * Initialize default categories in the database.
 */
export async function initializeDefaultCategoriesImpl(client, userId, instanceLogger = moduleLogger) {
  try {
    const now = new Date().toISOString();

    const createdCategories = [];

    for (const category of DEFAULT_CATEGORIES) {
      try {
        const { data: created, errors } = await client.models.CustomContentCategory.create({
          userId: userId,
          name: category.name,
          description: category.description,
          icon: category.icon,
          color: category.color,
          isDefault: category.isDefault,
          createdAt: now,
          updatedAt: now
        });

        if (errors && errors.length > 0) {
          instanceLogger.error(`Error creating category ${category.name}:`, errors);
        } else if (created) {
          createdCategories.push({
            id: created.id,
            value: created.name,
            label: created.name,
            name: created.name,
            description: created.description,
            icon: ICON_MAP[created.icon] || BookOpen,
            iconName: created.icon,
            color: created.color,
            isDefault: created.isDefault
          });
        }
      } catch (error) {
        instanceLogger.error(`Error creating category ${category.name}:`, error);
      }
    }

    return createdCategories.length > 0 ? createdCategories : getDefaultCategories();
  } catch (error) {
    instanceLogger.error('Error initializing default categories:', error);
    return getDefaultCategories();
  }
}

/**
 * Create a new custom category.
 *
 * NOTE: A same-named instance method also exists on CustomContentService
 * (`createCategory`) that uses the authenticated user's id implicitly. This
 * static variant takes userId explicitly for programmatic use — kept for
 * backward compat with the merged CustomContentCategoryService API.
 */
export async function createCategoryStaticImpl(client, userId, categoryData, instanceLogger = moduleLogger) {
  try {
    const now = new Date().toISOString();

    const { data: created, errors } = await client.models.CustomContentCategory.create({
      userId: userId,
      name: categoryData.name,
      description: categoryData.description || '',
      icon: categoryData.icon || 'BookOpen',
      color: categoryData.color || '#6B7280',
      isDefault: false,
      createdAt: now,
      updatedAt: now
    });

    if (errors && errors.length > 0) {
      return createNetworkError(errors[0].message || 'Failed to create category');
    }

    return {
      id: created.id,
      value: created.name,
      label: created.name,
      name: created.name,
      description: created.description,
      icon: ICON_MAP[created.icon] || BookOpen,
      iconName: created.icon,
      color: created.color,
      isDefault: false
    };
  } catch (error) {
    instanceLogger.error('Error creating category:', error);
    return createNetworkError('Failed to create category', error);
  }
}

/**
 * Update a category.
 */
export async function updateCategoryImpl(client, categoryId, updates, instanceLogger = moduleLogger) {
  try {
    const now = new Date().toISOString();

    const { data: updated, errors } = await client.models.CustomContentCategory.update({
      id: categoryId,
      ...updates,
      updatedAt: now
    });

    if (errors && errors.length > 0) {
      return createNetworkError(errors[0].message || 'Failed to update category');
    }

    return updated;
  } catch (error) {
    instanceLogger.error('Error updating category:', error);
    return createNetworkError('Failed to update category', error);
  }
}

/**
 * Delete a category (only if not default and not in use).
 */
export async function deleteCategoryImpl(client, categoryId, instanceLogger = moduleLogger) {
  try {
    const { data: content } = await client.models.CustomContent.list({
      filter: { category: { eq: categoryId } }
    });

    if (content && content.length > 0) {
      return createValidationError('Cannot delete category that is in use. Please reassign content first.');
    }

    const { errors } = await client.models.CustomContentCategory.delete({
      id: categoryId
    });

    if (errors && errors.length > 0) {
      return createNetworkError(errors[0].message || 'Failed to delete category');
    }

    return true;
  } catch (error) {
    instanceLogger.error('Error deleting category:', error);
    return createNetworkError('Failed to delete category', error);
  }
}

/**
 * Get category counts (how many content items per category).
 *
 * Receives `getCategoriesFn` so the facade can pass its own getCategories
 * binding (which uses its static `client` field). This preserves the
 * pre-decomposition behavior where `getCategoryCounts` called `this.getCategories`.
 */
export async function getCategoryCountsImpl(getCategoriesFn, userId, customContent, instanceLogger = moduleLogger) {
  try {
    const categories = await getCategoriesFn(userId);

    const counts = {};
    customContent.forEach(item => {
      counts[item.category] = (counts[item.category] || 0) + 1;
    });

    const uniqueCategories = [];
    const seenNames = new Set();

    for (const cat of categories) {
      if (!seenNames.has(cat.name)) {
        seenNames.add(cat.name);
        uniqueCategories.push(cat);
      }
    }

    return [
      { id: 'all', name: 'All Categories', count: customContent.length },
      ...uniqueCategories.map(cat => ({
        id: cat.name,
        name: cat.name,
        count: counts[cat.name] || 0,
        icon: cat.icon,
        color: cat.color
      }))
    ];
  } catch (error) {
    instanceLogger.error('Error getting category counts:', error);
    return [{ id: 'all', name: 'All Categories', count: 0 }];
  }
}

/**
 * Infer section from category for backward compatibility and automatic
 * organization. US-040: case-insensitive matching with comprehensive
 * category coverage. Pure function.
 */
export function inferSectionFromCategory(category) {
  if (!category) return 'General';

  const normalizedCategory = category.toLowerCase().trim();

  const categoryToSectionMap = {
    // Market-related categories → Market Overview or Market Analysis
    'market analysis': 'Market Analysis',
    'market-analysis': 'Market Analysis',
    'marketanalysis': 'Market Analysis',
    'analysis': 'Market Analysis',
    'market news': 'Market Overview',
    'market-news': 'Market Overview',
    'marketnews': 'Market Overview',
    'market overview': 'Market Overview',
    'market-overview': 'Market Overview',
    'market': 'Market Overview',

    // Investment-related categories → Investment Insights
    'investment ideas': 'Investment Insights',
    'investment-ideas': 'Investment Insights',
    'investment': 'Investment Insights',
    'investment advice': 'Investment Insights',
    'investment-advice': 'Investment Insights',
    'investment research': 'Investment Insights',
    'investment-research': 'Investment Insights',
    'investing': 'Investment Insights',
    'stocks': 'Investment Insights',
    'bonds': 'Investment Insights',

    // Economic categories → Economic Outlook
    'economic data': 'Economic Outlook',
    'economic-data': 'Economic Outlook',
    'economics': 'Economic Outlook',
    'economy': 'Economic Outlook',
    'economic': 'Economic Outlook',
    'macro': 'Economic Outlook',
    'macroeconomic': 'Economic Outlook',

    // Portfolio categories → Portfolio Updates
    'portfolio management': 'Portfolio Updates',
    'portfolio-management': 'Portfolio Updates',
    'portfolio': 'Portfolio Updates',
    'asset allocation': 'Portfolio Updates',
    'asset-allocation': 'Portfolio Updates',
    'rebalancing': 'Portfolio Updates',
    'wealth management': 'Portfolio Updates',
    'wealth-management': 'Portfolio Updates',
    'wealth': 'Portfolio Updates',

    // Education categories → Education Corner
    'education': 'Education Corner',
    'financial education': 'Education Corner',
    'financial-education': 'Education Corner',
    'personal finance': 'Education Corner',
    'personal-finance': 'Education Corner',
    'financial literacy': 'Education Corner',
    'financial-literacy': 'Education Corner',
    'retirement': 'Education Corner',
    'retirement planning': 'Education Corner',
    'retirement-planning': 'Education Corner',
    'college planning': 'Education Corner',
    'college-planning': 'Education Corner',

    // Communication categories → Advisor Commentary
    'client communication': 'Advisor Commentary',
    'client-communication': 'Advisor Commentary',
    'communication': 'Advisor Commentary',
    'firm information': 'Advisor Commentary',
    'firm-information': 'Advisor Commentary',
    'firm': 'Advisor Commentary',
    'advisor commentary': 'Advisor Commentary',
    'advisor-commentary': 'Advisor Commentary',
    'commentary': 'Advisor Commentary',
    'philosophy': 'Advisor Commentary',
    'values': 'Advisor Commentary',

    // Personal Note → Advisor Voice (for {{personal_note}} placeholder)
    'personal note': 'Advisor Voice',
    'personal-note': 'Advisor Voice',
    'personal notes': 'Advisor Voice',
    'advisor note': 'Advisor Voice',
    'advisor-note': 'Advisor Voice',

    // Human Touch → Advisor Voice (for {{human_touch}} placeholder)
    'human touch': 'Advisor Voice',
    'human-touch': 'Advisor Voice',
    'book recommendation': 'Advisor Voice',
    'book-recommendation': 'Advisor Voice',
    'personal': 'Advisor Voice',

    // Compliance and Action Items → Action Items
    'compliance & tax': 'Action Items',
    'compliance-tax': 'Action Items',
    'compliance': 'Action Items',
    'tax planning': 'Action Items',
    'tax-planning': 'Action Items',
    'tax': 'Action Items',
    'regulatory': 'Action Items',
    'legal': 'Action Items',
    'action items': 'Action Items',
    'action-items': 'Action Items',
    'tasks': 'Action Items',
    'reminders': 'Action Items'
  };

  return categoryToSectionMap[normalizedCategory] || 'General';
}
